import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';
import QuizIntro from './components/QuizIntro';
import QuizQuestion from './components/QuizQuestion';
import QuizResult from './components/QuizResult';
import { questions, results } from './data/quizData';

export default function TestPage() {
  const [started, setStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [showResult, setShowResult] = useState(false);

  const handleStart = () => {
    setStarted(true);
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
  };

  const handleAnswer = (answer: string) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setAnswers(answers.slice(0, -1));
    }
  };

  const handleRestart = () => {
    setStarted(false);
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
  };

  const calculateResult = () => {
    const scores = { A: 0, B: 0, C: 0, D: 0 };
    
    answers.forEach(answer => {
      scores[answer as keyof typeof scores]++;
    });

    const maxScore = Math.max(scores.A, scores.B, scores.C, scores.D);
    const winners = Object.entries(scores)
      .filter(([, score]) => score === maxScore)
      .map(([key]) => key);

    if (winners.length === 1) {
      return winners[0];
    }

    const priority = ['B', 'A', 'C', 'D'];
    for (const key of priority) {
      if (winners.includes(key)) {
        return key;
      }
    }

    return 'B';
  };

  const resultKey = calculateResult();

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Test: ¿Qué perro es perfecto para ti? | AULA K9",
    "description": "Test interactivo para descubrir qué raza de perro se adapta mejor a tu estilo de vida. Responde 12 preguntas y obtén recomendaciones personalizadas.",
    "url": `${import.meta.env.VITE_SITE_URL || ''}/test`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "AULA K9 - Don Bandi El Taller de Pensar"
    }
  };

  return (
    <>
      <SEOHead
        title="Test: ¿Qué perro es perfecto para ti? | AULA K9 - Don Bandi"
        description="Test interactivo para descubrir qué raza de perro se adapta mejor a tu estilo de vida en España. Responde 12 preguntas y obtén recomendaciones personalizadas de razas según tu hogar, tiempo disponible y experiencia."
        keywords="test perro ideal, elegir raza perro, qué perro adoptar, test razas caninas, perro perfecto para mí, AULA K9"
        canonical="/test"
        schema={schema}
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
        <Header />
        <main className="flex-1 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {!started && !showResult && (
              <QuizIntro onStart={handleStart} />
            )}

            {started && !showResult && (
              <QuizQuestion
                question={questions[currentQuestion]}
                questionNumber={currentQuestion + 1}
                totalQuestions={questions.length}
                onAnswer={handleAnswer}
                onBack={handleBack}
                canGoBack={currentQuestion > 0}
              />
            )}

            {showResult && (
              <QuizResult
                resultKey={resultKey}
                result={results[resultKey as keyof typeof results]}
                onRestart={handleRestart}
              />
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}
